import { useState, useEffect, useRef, useCallback } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';
import { motion } from 'framer-motion';

interface SearchBarProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  autoFocus?: boolean;
  className?: string;
}

const placeholders = [
  'Search skills... e.g. "code review"',
  'Search skills... e.g. "git workflow"',
  'Search skills... e.g. "testing"',
  'Search skills... e.g. "documentation"',
  'Search skills... e.g. "refactoring"',
];

export default function SearchBar({
  value: controlledValue,
  onChange,
  placeholder,
  debounceMs = 300,
  autoFocus = false,
  className = '',
}: SearchBarProps) {
  const [internalValue, setInternalValue] = useState(controlledValue || '');
  const [isFocused, setIsFocused] = useState(false);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cycle placeholder text
  useEffect(() => {
    if (placeholder) return; // Don't cycle if custom placeholder provided
    const interval = setInterval(() => {
      setPlaceholderIdx((prev) => (prev + 1) % placeholders.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [placeholder]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Sync controlled value
  useEffect(() => {
    if (controlledValue !== undefined) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue]);

  const handleChange = useCallback(
    (val: string) => {
      setInternalValue(val);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onChange(val);
      }, debounceMs);
    },
    [onChange, debounceMs]
  );

  const handleClear = useCallback(() => {
    setInternalValue('');
    onChange('');
    inputRef.current?.focus();
  }, [onChange]);

  return (
    <motion.div
      className={`relative group ${className}`}
      initial={false}
      animate={isFocused ? { scale: 1.01 } : { scale: 1 }}
      transition={{ duration: 0.2 }}
    >
      {/* Glow border effect */}
      <div
        className={`absolute -inset-0.5 rounded-2xl transition-opacity duration-500 ${
          isFocused ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.3), rgba(168, 85, 247, 0.3))',
          filter: 'blur(8px)',
        }}
      />

      <div
        className={`relative flex items-center gap-3 px-5 py-4 rounded-2xl transition-all duration-300 ${
          isFocused
            ? 'glass-strong border-brand-500/30'
            : 'glass border-white/5'
        }`}
      >
        <FiSearch
          size={20}
          className={`shrink-0 transition-colors duration-300 ${
            isFocused ? 'text-brand-400' : 'text-slate-500'
          }`}
        />
        <input
          ref={inputRef}
          type="text"
          value={internalValue}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder || placeholders[placeholderIdx]}
          autoFocus={autoFocus}
          className="flex-1 bg-transparent text-white placeholder-gray-500 text-base outline-none"
        />
        {internalValue && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={handleClear}
            className="shrink-0 p-1 rounded-lg text-slate-500 hover:text-white hover:bg-white/10 transition-colors"
          >
            <FiX size={16} />
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}
